import React, { useState, useEffect, useMemo } from "react";
import { findBestMatch } from "../utils/fuzzyMatch";
import { ChevronDown, ArrowRight, ArrowLeft, AlertTriangle, X } from "lucide-react";
import { SvgAsset19, SvgAsset20 } from "../../../shared/generated-icons/phase2__components__steps__DataSetup__index.icons";

interface Column {
  id: string;
  label: string;
  required?: boolean;
}

interface ColumnMapperProps {
  systemColumns: Column[];
  csvHeaders: string[];
  onMappingComplete: (
    mapping: { [key: string]: string },
    fixedValues: { [key: string]: string }
  ) => void;
  initialMapping?: { [key: string]: string };
  initialFixedValues?: { [key: string]: string };
  autoApplied?: boolean;
  onCancel: () => void;
  sheetName: string;
  sheetType?:
    | "STUDENT"
    | "PARENT"
    | "STUDENT_PARENT"
    | "STAFF"
    | "FEE_ARREARS"
    | "IGNORE"
    | null;
  currentSheetIndex?: number;
  processingQueue?: any[];
}

const ColumnMapper: React.FC<ColumnMapperProps> = ({
  systemColumns,
  csvHeaders,
  onMappingComplete,
  initialMapping,
  initialFixedValues,
  sheetType,
  onCancel,
  autoApplied,
  currentSheetIndex,
  processingQueue,
}) => {
  const [mapping, setMapping] = useState<{ [key: string]: string }>(
    initialMapping || {}
  );
  const [fixedValues, setFixedValues] = useState<{ [key: string]: string }>(
    initialFixedValues || {}
  );
  const [inputModes, setInputModes] = useState<{
    [key: string]: "MAPPING" | "FIXED";
  }>({});
  const [missingRequiredFields, setMissingRequiredFields] = useState<string[]>([]);

  const processedHeaders = useMemo(() => {
    return csvHeaders
      .map((h) => String(h || "").trim())
      .filter((h) => h.length > 0);
  }, [csvHeaders]);

  useEffect(() => {
    if (initialMapping) setMapping(initialMapping);
    if (initialFixedValues) setFixedValues(initialFixedValues);

    if (initialFixedValues) {
      const newModes: any = {};
      Object.keys(initialFixedValues).forEach((k) => {
        if (initialFixedValues[k]) newModes[k] = "FIXED";
      });
      setInputModes((prev) => ({ ...prev, ...newModes }));
    }
  }, [initialMapping, initialFixedValues]);

  useEffect(() => {
    if (!initialMapping || Object.keys(initialMapping).length === 0) {
      const newMapping: { [key: string]: string } = {};
      const newFixedValues: { [key: string]: string } = {};
      const newInputModes: { [key: string]: "MAPPING" | "FIXED" } = {};

      systemColumns.forEach((col) => {
        const match =
          findBestMatch(col.label, processedHeaders) ||
          findBestMatch(col.id, processedHeaders);
        if (match) {
          newMapping[col.id] = match;
        } else if (col.id.includes("COUNTRY_CODE")) {
          newFixedValues[col.id] = "+91";
          newInputModes[col.id] = "FIXED";
        }
      });
      setMapping(newMapping);
      setFixedValues(newFixedValues);
      setInputModes(newInputModes);
    }
  }, [systemColumns, processedHeaders, initialMapping]);

  const handleSelectChange = (systemId: string, csvHeader: string) => {
    setMapping((prev) => ({ ...prev, [systemId]: csvHeader }));
  };

  const handleFixedValueChange = (systemId: string, value: string) => {
    setFixedValues((prev) => ({ ...prev, [systemId]: value }));
  };

  const toggleInputMode = (systemId: string) => {
    setInputModes((prev) => {
      const current = prev[systemId] || "MAPPING";
      const next = current === "MAPPING" ? "FIXED" : "MAPPING";

      if (next === "FIXED") {
        setMapping((m) => {
          const copy = { ...m };
          delete copy[systemId];
          return copy;
        });
      } else {
        setFixedValues((fv) => {
          const copy = { ...fv };
          delete copy[systemId];
          return copy;
        });
      }
      return { ...prev, [systemId]: next };
    });
  };

  const isParentColumn = (id: string) => {
    return (
      id.includes("father") ||
      id.includes("mother") ||
      id.includes("guardian") ||
      id.startsWith("parent_") ||
      id.includes("parentLanguage") ||
      id.includes("parentCountryCode")
    );
  };
  const isStaffColumn = (id: string) => id.startsWith("staff_");
  const isFeeArrearsColumn = (id: string) => id.startsWith("arrears_");

  const visibleColumns = useMemo(() => {
    let cols = systemColumns;
    if (sheetType === "STAFF") cols = cols.filter((c) => isStaffColumn(c.id));
    else if (sheetType === "FEE_ARREARS")
      cols = cols.filter((c) => isFeeArrearsColumn(c.id));
    else if (sheetType === "PARENT")
      cols = cols.filter((c) => isParentColumn(c.id));
    else if (sheetType === "STUDENT")
      cols = cols.filter(
        (c) =>
          !isParentColumn(c.id) &&
          !isStaffColumn(c.id) &&
          !isFeeArrearsColumn(c.id)
      );
    else if (sheetType === "STUDENT_PARENT")
      cols = cols.filter(
        (c) => !isStaffColumn(c.id) && !isFeeArrearsColumn(c.id)
      );

    return cols.sort((a, b) =>
      a.required === b.required ? 0 : a.required ? -1 : 1
    );
  }, [systemColumns, sheetType]);

  const handleComplete = () => {
    const missingRequired = visibleColumns.filter((col) => {
      if (!col.required) return false;
      const hasMapping = !!mapping[col.id];
      const hasFixed = !!fixedValues[col.id];
      return !hasMapping && !hasFixed;
    });

    if (missingRequired.length > 0) {
      setMissingRequiredFields(missingRequired.map((field) => field.label));
      return;
    }
    onMappingComplete(mapping, fixedValues);
  };

  return (
    <div
      style={{
        flex: "1",
        minHeight: "0",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div
        className="smx ai-column-mapper__scroll"
        style={{
          flex: "1",
          minHeight: "0",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "1.511vh",
        }}
      >
        {/* --- DYNAMIC HEADER BOX --- */}
        <div className="ai-column-mapper__hero"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1.385vh",
            padding: "1.637vh 1.889vh",
            borderRadius: "1.637vh",
            background: "linear-gradient(135deg,#F0EFFF,#F7F4FF)",
            border: "0.126vh solid #DED8FA",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: "4.282vh",
              height: "4.282vh",
              borderRadius: "1.259vh",
              background: "linear-gradient(135deg,#4F46E5,#7A4DF0)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
            }}
          >
            <SvgAsset19 />
          </div>
          <div>
            <div
              style={{
                fontWeight: "800",
                fontSize: "1.763vh",
                color: "#2E1F6B",
              }}
            >
              {autoApplied
                ? "AI recognised your columns automatically"
                : `Map columns for ${processingQueue?.[currentSheetIndex ?? 0]?.name}`}
            </div>
            <div style={{ fontSize: "1.511vh", color: "#5B5386" }}>
              Review the suggested mappings and confirm. Fields needing attention
              are highlighted.
            </div>
          </div>
        </div>

        {/* --- GRID HEADERS --- */}
        <div className="ai-column-mapper__labels"
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1fr 1.5fr 80px",
            gap: "1.511vh",
            padding: "0 1.763vh",
            fontSize: "1.322vh",
            fontWeight: "800",
            letterSpacing: ".05em",
            color: "#9AA2B4",
          }}
        >
          <span>ERP FIELD</span>
          <span>SAMPLE DATA</span>
          <span>MAPPED COLUMN / FIXED VALUE</span>
          <span style={{ textAlign: "center" }}>MODE</span>
        </div>

        {/* --- ROW CARDS CONTAINER --- */}
        <div className="ai-column-mapper__rows"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.008vh",
          }}
        >
          {visibleColumns.map((col) => {
            const isFixedMode = inputModes[col.id] === "FIXED";
            let sampleValue = "-";

            if (isFixedMode && fixedValues[col.id]) {
              sampleValue = fixedValues[col.id];
            } else if (
              mapping[col.id] &&
              processingQueue &&
              currentSheetIndex !== undefined
            ) {
              const mappedHeader = mapping[col.id];
              const headerIndex = csvHeaders.indexOf(mappedHeader);
              const firstDataRow =
                processingQueue[currentSheetIndex]?.data?.[1];

              if (
                headerIndex !== -1 &&
                firstDataRow &&
                firstDataRow[headerIndex] !== undefined
              ) {
                sampleValue = String(firstDataRow[headerIndex]);
              }
            }

            return (
              <div className="ai-column-mapper__row"
                key={col.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1fr 1.5fr 80px",
                  gap: "1.511vh",
                  padding: "1.385vh 1.763vh",
                  background: isFixedMode ? "#F9FAFB" : "#fff",
                  border: "0.126vh solid #EAECF3",
                  borderRadius: "1.511vh",
                  alignItems: "center",
                  transition: "background 0.2s",
                }}
              >
                {/* 1. ERP FIELD */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.882vh",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "700",
                      fontSize: "1.637vh",
                      color: "#20242E",
                    }}
                  >
                    {col.label}
                  </span>
                  {col.required && (
                    <span
                      style={{
                        fontSize: "1.134vh",
                        fontWeight: "800",
                        color: "#E5484D",
                        padding: "0.2vh 0.6vh",
                        borderRadius: "0.4vh",
                      }}
                    >
                      REQ
                    </span>
                  )}
                </div>

                {/* 2. SAMPLE DATA */}
                <span
                  style={{
                    fontSize: "1.574vh",
                    color: "#8A92A6",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={sampleValue}
                >
                  {sampleValue || "-"}
                </span>

                {/* 3. MAPPED COLUMN / INPUT */}
                <div style={{ position: "relative", width: "100%" }}>
                  {isFixedMode ? (
                    <input
                      type="text"
                      placeholder="Enter fixed value..."
                      value={fixedValues[col.id] || ""}
                      onChange={(e) =>
                        handleFixedValueChange(col.id, e.target.value)
                      }
                      style={{
                        width: "100%",
                        border: "0.126vh solid #C4EED6",
                        borderRadius: "0.756vh",
                        padding: "1.134vh 1.5vh",
                        fontSize: "1.511vh",
                        fontWeight: "600",
                        color: "#0B6B44",
                        background: "#F0FBF5",
                        outline: "none",
                      }}
                    />
                  ) : (
                    <>
                      <select
                        value={mapping[col.id] || ""}
                        onChange={(e) =>
                          handleSelectChange(col.id, e.target.value)
                        }
                        style={{
                          WebkitAppearance: "none",
                          appearance: "none",
                          width: "100%",
                          border: mapping[col.id]
                            ? "0.126vh solid #DED8FA"
                            : "0.126vh solid #FCA5A5",
                          borderRadius: "0.756vh",
                          padding: "1.134vh 3.5vh 1.134vh 1.5vh",
                          fontSize: "1.511vh",
                          fontWeight: "600",
                          color: mapping[col.id] ? "#000000" : "#DC2626",
                          background: mapping[col.id] ? "#F7F4FF" : "#FEF2F2",
                          outline: "none",
                          cursor: "pointer",
                        }}
                      >
                        <option value="">⚠ Select column...</option>
                        {processedHeaders.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        style={{
                          position: "absolute",
                          right: "1.2vh",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "1.6vh",
                          height: "1.6vh",
                          color: mapping[col.id] ? "#7A4DF0" : "#DC2626",
                          pointerEvents: "none",
                        }}
                      />
                    </>
                  )}
                </div>

                {/* 4. MODE TOGGLE */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <div
                    onClick={() => toggleInputMode(col.id)}
                    title={
                      isFixedMode
                        ? "Switch to Mapping Mode"
                        : "Switch to Fixed Value Mode"
                    }
                    style={{
                      width: "3.778vh",
                      height: "2.015vh",
                      borderRadius: "1.008vh",
                      background: isFixedMode ? "#0FA968" : "#E2E8F0",
                      position: "relative",
                      cursor: "pointer",
                      transition: "background 0.3s ease",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "0.252vh",
                        left: isFixedMode ? "2.015vh" : "0.252vh",
                        width: "1.511vh",
                        height: "1.511vh",
                        background: "#fff",
                        borderRadius: "50%",
                        transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: "0 0.126vh 0.252vh rgba(0,0,0,0.15)",
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* --- BOTTOM ACTIONS --- */}
      <div
        className="ai-wizard-actions"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "1.134vh",
          paddingTop: "2vh",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.756vh",
            padding: "1.511vh 3.023vh",
            border: "0.126vh solid #EAECF3",
            borderRadius: "1.511vh",
            background: "#fff",
            color: "#5A6173",
            fontSize: "1.763vh",
            fontWeight: "800",
            cursor: "pointer",
          }}
        >
          <ArrowLeft style={{ width: "2vh", height: "2vh" }} /> Back
        </button>
        <button
          type="button"
          onClick={handleComplete}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "1.134vh",
            padding: "1.511vh 3.023vh",
            border: "none",
            borderRadius: "1.511vh",
            background: "linear-gradient(135deg,#4F46E5,#7A4DF0)",
            color: "#fff",
            fontSize: "1.763vh",
            fontWeight: "800",
            cursor: "pointer",
            boxShadow: "0 1.259vh 3.275vh rgba(90,60,220,.32)",
          }}
        >
          Confirm & import
          <SvgAsset20 />
        </button>
      </div>

      {missingRequiredFields.length > 0 && (
        <div
          className="ai-mapping-modal__backdrop fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-6"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setMissingRequiredFields([]);
          }}
        >
          <section
            className="ai-mapping-modal relative w-full max-w-lg rounded-2xl bg-white p-7 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="missing-mapping-title"
          >
            <button
              type="button"
              className="ai-mapping-modal__close absolute right-4 top-4"
              onClick={() => setMissingRequiredFields([])}
              aria-label="Close missing fields dialog"
            >
              <X aria-hidden="true" />
            </button>

            <div className="ai-mapping-modal__heading">
              <span className="ai-mapping-modal__icon" aria-hidden="true">
                <AlertTriangle />
              </span>
              <div>
                <span className="ai-mapping-modal__eyebrow">MAPPING REQUIRED</span>
                <h2 id="missing-mapping-title">Complete required fields</h2>
              </div>
            </div>

            <p className="ai-mapping-modal__copy">
              Map these fields to a file column or provide a fixed value before
              continuing.
            </p>

            <div className="ai-mapping-modal__fields" aria-label="Missing required fields">
              {missingRequiredFields.map((field) => (
                <span key={field}>{field}</span>
              ))}
            </div>

            <div className="ai-mapping-modal__actions">
              <button type="button" onClick={() => setMissingRequiredFields([])}>
                Review mappings
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default ColumnMapper;
